import React, { useCallback } from 'react'
import { Input } from 'antd'
import { TextAreaProps } from 'antd/es/input'
import { clearIfWhitespaceOnly, normalizeWhitespace } from 'utils/inputSanitization'

const { TextArea } = Input

/**
 * SanitizedTextArea - A wrapper around Ant Design TextArea that sanitizes user input
 * to comply with FHIR validation rules.
 *
 * Two-phase sanitization:
 * 1. onChange: Normalizes illegal whitespace characters (e.g., non-breaking spaces → spaces)
 *    and clears whitespace-only values to prevent storing invalid data in form state
 * 2. onBlur: Additional sanitization pass to ensure whitespace-only values are cleared
 *
 * This prevents "non-whitespace content" errors from Medplum by ensuring strings
 * always contain at least one non-whitespace character, as required by FHIR spec.
 */
function SanitizedTextArea({ onChange, onBlur, ...props }: TextAreaProps): JSX.Element {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      // Phase 1: Normalize whitespace on every keystroke
      let sanitizedValue = normalizeWhitespace(e.target.value)

      // Phase 1.5: Clear whitespace-only values immediately to prevent storing
      // invalid values in form state. This prevents "non-whitespace content" errors
      // from Medplum when forms are submitted before blur.
      sanitizedValue = clearIfWhitespaceOnly(sanitizedValue)

      // Update the event with sanitized value
      const sanitizedEvent = {
        ...e,
        target: {
          ...e.target,
          value: sanitizedValue
        }
      }

      onChange?.(sanitizedEvent as React.ChangeEvent<HTMLTextAreaElement>)
    },
    [onChange]
  )

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      // Phase 2: Clear if whitespace-only on blur
      const clearedValue = clearIfWhitespaceOnly(e.target.value)

      // If value changed after clearing whitespace, trigger onChange to update form
      if (clearedValue !== e.target.value) {
        // Create a complete synthetic event by spreading the original focus event
        // and overriding the target value
        const changeEvent = {
          ...e,
          target: {
            ...e.target,
            value: clearedValue
          },
          currentTarget: {
            ...e.currentTarget,
            value: clearedValue
          }
        } as React.ChangeEvent<HTMLTextAreaElement>
        onChange?.(changeEvent)

        // Also update the blur event to reflect the cleared value
        const updatedBlurEvent = {
          ...e,
          target: {
            ...e.target,
            value: clearedValue
          },
          currentTarget: {
            ...e.currentTarget,
            value: clearedValue
          }
        }
        onBlur?.(updatedBlurEvent)
      } else {
        // Call original onBlur handler with original event
        onBlur?.(e)
      }
    },
    [onBlur, onChange]
  )

  return <TextArea {...props} onChange={handleChange} onBlur={handleBlur} />
}

export default SanitizedTextArea
